import type { FulfillmentConfiguration } from './fulfillment-configuration';

/**
 * THE OFMOS CONFIGURATION'S BROWSER HALF (DESIGN.md Part 1 priority 7: "the OFMOS configuration
 * (seed) names them").
 *
 * The seed (`packages/server/src/seed/`) writes OFMOS's objects, fields, checkpoint deadlines,
 * health rules and views into the database, and the pages read those as data. What the pages
 * need beside that data is this one object. It lives in `shared` because `packages/web` may not
 * import server code (`eslint.config.js`, the layering rule) and `shared` is where the seed and
 * the browser already share OFMOS's lists (`dashboard.ts`); the seed's own test holds the two
 * halves together (`seed/model.test.ts`, "the configured onboarding steps").
 *
 * EVERY VALUE IS THE ONE THE PLATFORM CODE HELD BEFORE BR-NAMES (4 October 2026), moved
 * unchanged, so the OFMOS pages look and behave as they did. The comments name where each came
 * from; `docs/decisions/DEVIATIONS-FULFILLMENT.md`, section BR-NAMES, lists the old homes.
 */
export const OFMOS_FULFILLMENT: FulfillmentConfiguration = {
  /**
   * THE NINE STEPS, IN OFMOS'S ORDER (fulfillment contract COMMON section 4; register D5),
   * which is also the seed's declaration order on `cohort_enrollment`. The short names are the
   * Checklist mockup's column names (`onboarding-v2.html:156`) plus the register's O4 word
   * "Gameplan read"; the purposes are OFMOS's own sentences from its handover tool, without
   * their timing half, which the checkpoint setting generates; the actors are register A9: the
   * client acts on the intake and the video notes, the team on the other seven; a step the
   * call blocks reads "After the call" in its cell (register O4, resolved).
   *
   * The sixth step carries the name of the product it introduces. It is OFMOS's step name
   * (D5) and lives here and in the seed's field label, never in a platform component.
   */
  onboardingSteps: [
    { field: 'form_submitted_at', shortName: 'Intake', actor: 'client' },
    { field: 'gameplan_written_at', shortName: 'Gameplan', actor: 'us' },
    {
      field: 'gameplan_read_at',
      shortName: 'Gameplan read',
      purpose: 'The CSM ticks this after reading the gameplan, before the onboarding call.',
      actor: 'us',
    },
    {
      field: 'onboarding_call_at',
      shortName: 'Call',
      actor: 'us',
      blockedWords: 'After the call',
    },
    { field: 'introduced_at', shortName: 'Intro', actor: 'us' },
    {
      field: 'oliver_shown_at',
      shortName: 'Oliver AI',
      purpose: 'Show the client how to use Oliver AI in their group.',
      actor: 'us',
    },
    { field: 'checkin_48h_at', shortName: '48h check-in', actor: 'us' },
    {
      field: 'course_notes_at',
      shortName: 'Video notes',
      purpose: 'The client writes notes on at least one course video.',
      actor: 'client',
    },
    { field: 'checkin_7d_at', shortName: '7-day check-in', actor: 'us' },
  ],
  /**
   * A 16-week program (register D9, "past week 16"; common contract section 4, the week capped
   * at `contract_weeks`) whose renewal status shows from week 13 (register D6).
   */
  program: { weeks: 16, renewalFromWeek: 13 },
  /**
   * Decision page item 10.2 (register A7-ALL): the daily intake pull turns "Intakes checked"
   * orange after two days without one, the proposal of that item ("Vorschlag 2 Tage").
   */
  intakesStaleHours: 48,
  /** Register D9: "Telegram stale threshold above 12 hours". */
  telegramStaleHours: 12,
  /**
   * Register A7 12.5 B (decision page item 12.5, "Die Schwelle ist eine Einstellung (Vorschlag 2
   * Wochen)"): a check-in older than two weeks reads in the late tone on the client page.
   */
  checkinStaleWeeks: 2,
  /**
   * The register's level words (P3, components page sections 1 to 3) and their counted form in
   * the Clients composition legend, the Paused group of A13, and the calm groups P3 starts
   * folded.
   */
  health: {
    levels: { red: 'Needs attention', yellow: 'Keep an eye on', green: 'On track' },
    counted: { red: 'need attention', yellow: 'keep an eye on', green: 'on track' },
    paused: 'Paused',
    folded: ['yellow', 'green'],
  },
  /** Register P3: "Group by (Health default, None, Program week, Coach)". */
  groupBy: [
    { value: 'health', label: 'Health' },
    { value: 'none', label: 'None' },
    { value: 'week', label: 'Program week' },
    { value: 'coach', label: 'Coach' },
  ],
  /**
   * Decision page item 13.1 = B (owner, A7-ALL): "Ben als Owner sieht 'Everyone'; Coaches sehen
   * als Standard 'Mine'. Je Rolle eine Einstellung, nicht ein Wert fuer alle." The labels are
   * the seeded access roles.
   */
  ownerMenuStart: { Owner: 'all', Coach: 'mine' },
  /** OFMOS's sentence for the pane (fulfillment round 2 findings, F4). */
  welcomeMessagesDescription:
    "The message Oliver's onboarding sends when a new client joins. One per package and language.",
  /** Register A8-3: "Settings gets the section with the configured label "Oliver AI"". */
  botSettingsTitle: 'Oliver AI',
};
